// src/services/apiService.js

import {
    mapPreferenceToGroupApiFormat,
    mapPreferenceToTeacherApiFormat,
    mapPreferenceFromApiFormat
} from '../utils/preferenceMappers';
import logger from '../utils/logger';
import configService from './configService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://lebaptiste.com/schedule-pro/api';
const PUBLIC_API_BASE_URL = process.env.REACT_APP_PUBLIC_API_BASE_URL || 'https://lebaptiste.com/schedule-pro/public/institutions';

/** Avoid `.../institutions//feuille/...` when env has a trailing slash (Express returns HTML 404 → UI shows "Not Found"). */
const joinBaseUrlAndPath = (baseUrl, path) => {
    const base = (baseUrl || '').replace(/\/+$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
};

// These will be set by initializeApiService
let _getAuthToken = () => null;
let _getInstitutionUrlEndpoint = () => null;
let _refreshToken = () => null;
let _onAccessTokenRequired = null;

export const initializeApiService = (authTokenGetter, institutionUrlEndpointGetter, tokenRefresher = null, onAccessTokenRequired = null) => {
    _getAuthToken = authTokenGetter;
    _getInstitutionUrlEndpoint = institutionUrlEndpointGetter;
    _refreshToken = tokenRefresher;
    _onAccessTokenRequired = onAccessTokenRequired;
    logger.debug('[apiService] Initialized with token and endpoint getters.');
};

// Enhanced fetchApi internal helper with token refresh
const _internalFetchApi = async (endpointPath, method = 'GET', body = null, queryParams = null, options = {}, isRetry = false) => {
    // Serverless short-circuit
    try {
        const noServer = await configService.isNoServerEnabled();
        if (noServer) {
            const { mockFetch } = await import('./mockApiService');
            logger.debug(`[apiService] NO_SERVER mode -> mockFetch ${method} ${endpointPath}`);
            return await mockFetch(endpointPath, method, body, queryParams, options);
        }
    } catch (e) {
        // If config fails, continue with network fallback
    }
    const makeRequest = async (authToken) => {
        const institutionUrlSegment = _getInstitutionUrlEndpoint(); // e.g., "maple-high" or "maple-high/drafts/abc1234" for shared drafts

        // Determine if this request should be public shared-draft schedule fetch
        const isSharedDraftContext = typeof institutionUrlSegment === 'string' && institutionUrlSegment.includes('/drafts/');
        const isScheduleEntriesPath = typeof endpointPath === 'string' && endpointPath.startsWith('/schedule-entries');
        const isSharedDraftScheduleRequest = isSharedDraftContext && isScheduleEntriesPath;

        // Force public access for shared draft schedule-entries to avoid using token
        if (isSharedDraftScheduleRequest) {
            authToken = null;
        }

        let baseUrl;
        let finalEndpointPath = endpointPath; // e.g., "/schedule-entries"
        const headers = { ...options.headers || {} };

        if (authToken) {
            // Authenticated API call
            baseUrl = API_BASE_URL; // Uses /api/...
            headers['Authorization'] = `Bearer ${authToken}`;
            logger.debug(`[apiService] Using AUTHENTICATED API: ${baseUrl}${finalEndpointPath}`);
        } else if (institutionUrlSegment) {
            // Public API call for a specific institution
            baseUrl = PUBLIC_API_BASE_URL; // Uses /public/institutions/...
            // Use full segment for shared-draft schedule entries; otherwise trim to institution only
            let effectiveInstitutionSegment = institutionUrlSegment;
            if (isSharedDraftContext && !isScheduleEntriesPath) {
                effectiveInstitutionSegment = institutionUrlSegment.split('/drafts/')[0];
            }
            finalEndpointPath = `/${effectiveInstitutionSegment}${endpointPath.startsWith('/') ? '' : '/'}${endpointPath}`;
            logger.debug(`[apiService] Using PUBLIC API for ${effectiveInstitutionSegment}: ${baseUrl}${finalEndpointPath}`);
        } else {
            // No token and no specific institution endpoint for public access.
            logger.warn(`[apiService] Call to ${endpointPath} attempted without token and without a specific institution URL endpoint. This might lead to an error or unintended behavior.`);
            baseUrl = API_BASE_URL;
        }

        // Only set Content-Type to application/json if not already set and if body is not null
        const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
        if (body && !headers['Content-Type'] && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            if (!isFormData) {
                headers['Content-Type'] = 'application/json';
            }
        }

        let url = joinBaseUrlAndPath(baseUrl, finalEndpointPath);

        if (queryParams) {
            const queryString = new URLSearchParams(queryParams).toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }

        const fetchOptions = {
            method,
            headers,
            body: body ? (headers['Content-Type'] === 'application/json' ? JSON.stringify(body) : body) : null,
        };

        logger.debug(`[apiService] Requesting: ${method} ${url}`, body ? `with body` : '', headers['Authorization'] ? 'with token' : 'without token');
        if (body && headers['Content-Type'] !== 'application/json') {
            logger.debug(`[apiService] Sending non-JSON body with Content-Type: ${headers['Content-Type']}`);
        }

        const response = await fetch(url, fetchOptions);
        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (parseError) {
                const responseText = await response.text().catch(() => 'Could not read error response text.');
                errorData = { message: response.statusText || 'Unknown API error', details: responseText };
            }
            const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
            error.status = response.status;
            error.response = { data: errorData };
            error.code = errorData.code;
            error.details = errorData.details;
            logger.error(`[apiService] Error Response (${response.status}) from ${url}:`, errorData);
            if (response.status === 401 && _onAccessTokenRequired) {
                const msg = errorData.message || '';
                if (/access token is require/i.test(msg)) {
                    try {
                        _onAccessTokenRequired();
                    } catch (cbErr) {
                        logger.error('[apiService] onAccessTokenRequired failed:', cbErr);
                    }
                }
            }
            throw error;
        }
        if (response.status === 204) {
            logger.debug(`[apiService] Success (No Content): ${method} ${url}`);
            return null;
        }
        const data = await response.json();
        logger.debug(`[apiService] Success: ${method} ${url}`);
        return data;
    };

    try {
        const token = _getAuthToken();
        return await makeRequest(token);
    } catch (error) {
        // Check if this is a token expiration error and we haven't already retried
        const isTokenExpired = error.status === 401 && 
            (error.message?.includes('expired') || error.message?.includes('id-token-expired'));
        
        if (isTokenExpired && !isRetry && _refreshToken) {
            logger.warn('[apiService] Token expired, attempting to refresh and retry');
            try {
                const freshToken = await _refreshToken();
                return await makeRequest(freshToken);
            } catch (refreshError) {
                logger.error('[apiService] Token refresh failed:', refreshError);
                throw error; // Throw the original error
            }
        }
        
        throw error;
    }
};

/**
 * Fetches schedule entries.
 * The token and institutionUrlEndpoint are handled internally by _internalFetchApi.
 * @param {object} [filters={}] - Filtering options for the schedule entries (e.g., startDate, teacherId).
 * @param {string} [include=''] - Comma-separated list of related resources to include.
 * @returns {Promise<Array>} - A promise that resolves to the schedule entries.
 */
export const getScheduleEntries = (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return _internalFetchApi(`/schedule-entries${queryString ? `?${queryString}` : ''}`);
};

// --- Draft Sharing (authenticated) ---
export const createOrRotateDraftShare = (draftName, options = {}) => {
    if (!draftName) throw new Error('draftName is required');
    const body = { draftName, ...(options.expiresAt ? { expiresAt: options.expiresAt } : {}), rotate: !!options.rotate };
    return _internalFetchApi('/drafts/share', 'POST', body);
};

export const getDraftShare = (draftName) => {
    if (!draftName) throw new Error('draftName is required');
    return _internalFetchApi(`/drafts/share/${encodeURIComponent(draftName)}`, 'GET');
};

export const revokeDraftShare = (draftName) => {
    if (!draftName) throw new Error('draftName is required');
    return _internalFetchApi(`/drafts/share/${encodeURIComponent(draftName)}`, 'DELETE');
};

// --- Teachers ---
export const getTeachers = async (include = '') => { // Token removed
    const queryParams = include ? { include } : {};
    return _internalFetchApi('/teachers', 'GET', null, queryParams);
};

export const getTeacherById = async (teacherId, include = 'preferences,qualifiedSubjects') => { // Token removed
    if (!teacherId) throw new Error("Teacher ID is required.");
    const queryParams = include ? { include } : {};
    return _internalFetchApi(`/teachers/${teacherId}`, 'GET', null, queryParams);
};

export const addTeacher = (teacherData) => { // Token removed
    if (!teacherData || !teacherData.teacher_code || !teacherData.name || !teacherData.email) {
        throw new Error("Teacher code, name, and email are required.");
    }
    const body = {
        teacher_code: teacherData.teacher_code,
        name: teacherData.name,
        email: teacherData.email
    };
    if (teacherData.hasOwnProperty('tags')) body.tags = teacherData.tags;
    return _internalFetchApi('/teachers', 'POST', body);
};

export const updateTeacher = (teacherId, updatedData) => { // Token removed
    if (!teacherId) throw new Error("Teacher ID is required for update.");
    const body = {};
    if (updatedData.hasOwnProperty('teacher_code')) body.teacher_code = updatedData.teacher_code;
    if (updatedData.hasOwnProperty('name')) body.name = updatedData.name;
    if (updatedData.hasOwnProperty('email')) body.email = updatedData.email;
    if (updatedData.hasOwnProperty('tags')) body.tags = updatedData.tags;
    return _internalFetchApi(`/teachers/${teacherId}`, 'PUT', body);
};

export const deleteTeacher = (teacherId) => { // Token removed
    if (!teacherId) throw new Error("Teacher ID is required for deletion.");
    return _internalFetchApi(`/teachers/${teacherId}`, 'DELETE');
};

export const updateTeacherPreferences = (teacherId, preferencesData) => {
    if (!teacherId) throw new Error("Teacher ID is required.");
    if (!Array.isArray(preferencesData)) throw new Error("Preferences data must be an array.");
    
    // Log the incoming data for debugging
    logger.debug('[apiService] Updating preferences for teacher', teacherId, 'with data:', preferencesData);
    
    // Validate and format each preference
    const formattedPreferencesData = preferencesData.map(pref => {
        // Check if the preference has camelCase properties (frontend format)
        if (pref.startTime && pref.endTime) {
            // Convert from camelCase to snake_case for API
            return mapPreferenceToTeacherApiFormat(pref);
        }
        // Check if the preference already has snake_case properties (API format)
        else if (pref.start_time && pref.end_time) {
            // Already in correct format, validate and return as is
            if (!pref.day || !pref.status) {
                throw new Error(`Invalid preference format: ${JSON.stringify(pref)}`);
            }
            return pref;
        }
        // Invalid format
        else {
            throw new Error(`Invalid preference format: ${JSON.stringify(pref)}`);
        }
    });
    
    // Log the formatted data being sent to the API
    logger.debug('[apiService] Sending formatted preferences to API:', formattedPreferencesData);
    
    return _internalFetchApi(`/teachers/${teacherId}/preferences`, 'PUT', formattedPreferencesData);
};

export const updateTeacherQualifications = (teacherId, subjectIds) => { // Token removed
    if (!teacherId) throw new Error("Teacher ID is required.");
    if (!Array.isArray(subjectIds)) throw new Error("Subject IDs data must be an array.");
    return _internalFetchApi(`/teachers/${teacherId}/qualifications`, 'PUT', subjectIds);
};

// --- Groups ---
export const getGroups = async (include = '') => { // Token removed
    const queryParams = include ? { include } : {};
    return _internalFetchApi('/groups', 'GET', null, queryParams);
};

export const getGroupById = async (groupId, token, include = 'students,preferences') => {
    if (!groupId) throw new Error("Group ID is required.");
    const queryParams = { include };
    logger.debug("[apiService getGroupById] Fetching group with params:", { groupId, include, queryParams });
    const groupFromApi = await _internalFetchApi(`/groups/${groupId}`, 'GET', null, queryParams);
    logger.debug("[apiService getGroupById] Raw API response:", groupFromApi);
    if (groupFromApi && groupFromApi.preferences && Array.isArray(groupFromApi.preferences)) {
        groupFromApi.preferences = groupFromApi.preferences.map(mapPreferenceFromApiFormat);
    }
    logger.debug("[apiService getGroupById] Processed group data:", groupFromApi);
    return groupFromApi;
};

export const addGroup = (groupData) => {
    if (!groupData || !groupData.group_code || !groupData.name) {
        throw new Error("Group code and name are required.");
    }
    const body = { group_code: groupData.group_code, name: groupData.name };
    return _internalFetchApi('/groups', 'POST', body);
};

export const updateGroup = (groupId, updatedData) => {
    if (!groupId) throw new Error("Group ID is required.");
    const body = {};
    if (updatedData.hasOwnProperty('group_code')) body.group_code = updatedData.group_code;
    if (updatedData.hasOwnProperty('name')) body.name = updatedData.name;
    if (updatedData.hasOwnProperty('tags')) body.tags = updatedData.tags;
    return _internalFetchApi(`/groups/${groupId}`, 'PUT', body);
};

export const deleteGroup = (groupId) => {
    if (!groupId) throw new Error("Group ID is required for deletion.");
    return _internalFetchApi(`/groups/${groupId}`, 'DELETE');
};

export const addStudentToGroup = (groupId, studentId) => {
    if (!groupId || !studentId) throw new Error("Group ID and Student ID are required.");
    return _internalFetchApi(`/groups/${groupId}/students`, 'POST', { studentId });
};

export const addStudentsToGroupBatch = (groupId, students) => {
    if (!groupId || !students || !Array.isArray(students) || students.length === 0) {
        throw new Error("Group ID and students array are required.");
    }
    return _internalFetchApi(`/groups/${groupId}/students/batch`, 'POST', { students });
};

export const removeStudentFromGroup = (groupId, studentId) => {
    if (!groupId || !studentId) throw new Error("Group ID and Student ID are required.");
    return _internalFetchApi(`/groups/${groupId}/students/${studentId}`, 'DELETE');
};

export const updateGroupPreferences = (groupId, preferencesData) => {
    if (!groupId) throw new Error("Group ID is required.");
    if (!Array.isArray(preferencesData)) throw new Error("Preferences data must be an array.");
    const formattedPreferencesData = preferencesData.map(mapPreferenceToGroupApiFormat);
    return _internalFetchApi(`/groups/${groupId}/preferences`, 'PUT', formattedPreferencesData);
};

// --- Students ---
export const getStudents = async (filters = {}, include = '') => {
    const queryParams = { ...filters };
    if (include) queryParams.include = include;
    return _internalFetchApi('/students', 'GET', null, queryParams);
};

export const addStudent = (studentData) => {
    if (!studentData || !studentData.first_name || !studentData.family_name || !studentData.email) {
        throw new Error("Student first name, family name, and email are required.");
    }
    const body = {
        first_name: studentData.first_name,
        family_name: studentData.family_name,
        email: studentData.email,
        wants_emails: studentData.wants_emails
    };
    return _internalFetchApi('/students', 'POST', body);
};

export const updateStudentEmailPreference = (studentId, wantsEmails) => {
    if (!studentId) throw new Error("Student ID is required.");
    if (typeof wantsEmails !== 'boolean') throw new Error("wantsEmails must be a boolean value.");
    
    const body = {
        wants_emails: wantsEmails
    };
    return _internalFetchApi(`/students/${studentId}/email-preference`, 'PATCH', body);
};

// Public unsubscribe function (doesn't require authentication)
export const unsubscribeFromEmails = async (institutionUrlEndpoint, studentId) => {
    // Force a public API call by temporarily setting the institution URL endpoint and removing token
    const originalTokenGetter = _getAuthToken;
    const originalEndpointGetter = _getInstitutionUrlEndpoint;
    
    _getAuthToken = () => null; // Force no token to use public API
    _getInstitutionUrlEndpoint = () => institutionUrlEndpoint;
    
    try {
        const result = await _internalFetchApi(`/students/${studentId}/unsubscribe`, 'POST', null, null, {
            headers: {} // Ensure no auth headers are added
        });
        return result;
    } finally {
        // Restore the original getters
        _getAuthToken = originalTokenGetter;
        _getInstitutionUrlEndpoint = originalEndpointGetter;
    }
};

// Public function to get student status (doesn't require authentication)
export const getStudentStatus = async (institutionUrlEndpoint, studentId) => {
    // Force a public API call by temporarily setting the institution URL endpoint and removing token
    const originalTokenGetter = _getAuthToken;
    const originalEndpointGetter = _getInstitutionUrlEndpoint;
    
    _getAuthToken = () => null; // Force no token to use public API
    _getInstitutionUrlEndpoint = () => institutionUrlEndpoint;
    
    try {
        const result = await _internalFetchApi(`/students/${studentId}`, 'GET', null, null, {
            headers: {} // Ensure no auth headers are added
        });
        return result;
    } finally {
        // Restore the original getters
        _getAuthToken = originalTokenGetter;
        _getInstitutionUrlEndpoint = originalEndpointGetter;
    }
};

// ... (getStudentById, updateStudent, deleteStudent would follow the same pattern of removing token and using _internalFetchApi)

// --- Rooms ---
export const getRooms = () => {
    return _internalFetchApi('/rooms', 'GET');
};

export const getRoomById = (roomId, include = 'preferences') => {
    if (!roomId) throw new Error("Room ID is required.");
    const queryParams = include ? { include } : {};
    return _internalFetchApi(`/rooms/${roomId}`, 'GET', null, queryParams)
        .then(room => {
            // Transform preferences from API format to frontend format
            if (room.preferences && Array.isArray(room.preferences)) {
                room.preferences = room.preferences.map(pref => mapPreferenceFromApiFormat(pref));
            }
            return room;
        });
};

export const addRoom = (roomData) => {
    if (!roomData || !roomData.room_code || !roomData.name) {
        throw new Error("Room code and name are required.");
    }
    const body = { 
        room_code: roomData.room_code, 
        name: roomData.name,
        max_capacity: roomData.maxCapacity || null,
        is_handicap_accessible: roomData.isHandicapAccessible || false,
        equipment: roomData.equipment || null,
        notes: roomData.notes || null
    };
    return _internalFetchApi('/rooms', 'POST', body);
};

export const updateRoom = (roomId, updatedData) => {
    if (!roomId) throw new Error("Room ID is required.");
    
    const body = {};
    if (updatedData.hasOwnProperty('room_code')) body.room_code = updatedData.room_code;
    if (updatedData.hasOwnProperty('name')) body.name = updatedData.name;
    if (updatedData.hasOwnProperty('max_capacity')) body.max_capacity = updatedData.max_capacity || null;
    if (updatedData.hasOwnProperty('is_handicap_accessible')) body.is_handicap_accessible = updatedData.is_handicap_accessible;
    if (updatedData.hasOwnProperty('equipment')) body.equipment = updatedData.equipment || null;
    if (updatedData.hasOwnProperty('notes')) body.notes = updatedData.notes || null;
    
    return _internalFetchApi(`/rooms/${roomId}`, 'PUT', body);
};

export const deleteRoom = (roomId) => {
    if (!roomId) throw new Error("Room ID is required for deletion.");
    return _internalFetchApi(`/rooms/${roomId}`, 'DELETE');
};

export const updateRoomPreferences = (roomId, preferencesData) => {
    if (!roomId) throw new Error("Room ID is required.");
    if (!preferencesData) throw new Error("Preferences data is required.");

    // Transform preferences to match backend structure if needed
    const transformedData = Array.isArray(preferencesData) ? preferencesData : [preferencesData];
    
    return _internalFetchApi(`/rooms/${roomId}/preferences`, 'PUT', transformedData);
};

// --- Subjects ---
export const getSubjects = async () => {
    return _internalFetchApi('/subjects', 'GET');
};

export const getSubjectById = async (subjectId) => {
    if (!subjectId) throw new Error("Subject ID is required.");
    return _internalFetchApi(`/subjects/${subjectId}`, 'GET');
};

export const addSubject = (subjectData) => {
    if (!subjectData || !subjectData.name) {
        throw new Error("Subject name is required.");
    }
    const body = { 
        name: subjectData.name,
        color: subjectData.color // optional
    };
    // Include subject_code only if provided (for manual override)
    if (subjectData.subject_code) {
        body.subject_code = subjectData.subject_code;
    }
    // Include associatedRooms if provided
    if (subjectData.associatedRooms && Array.isArray(subjectData.associatedRooms)) {
        body.associatedRooms = subjectData.associatedRooms;
    }
    return _internalFetchApi('/subjects', 'POST', body);
};

export const updateSubject = (subjectId, updatedData) => {
    if (!subjectId) throw new Error("Subject ID is required for update.");
    const body = {};
    if (updatedData.hasOwnProperty('subject_code')) body.subject_code = updatedData.subject_code;
    if (updatedData.hasOwnProperty('name')) body.name = updatedData.name;
    if (updatedData.hasOwnProperty('color')) body.color = updatedData.color;
    if (updatedData.hasOwnProperty('associatedRooms')) body.associatedRooms = updatedData.associatedRooms;
    return _internalFetchApi(`/subjects/${subjectId}`, 'PUT', body);
};

export const deleteSubject = (subjectId) => {
    if (!subjectId) throw new Error("Subject ID is required for deletion.");
    return _internalFetchApi(`/subjects/${subjectId}`, 'DELETE');
};

export const deleteAllSubjects = async () => {
    // Honor mock mode
    if (await configService.isNoServerEnabled()) {
        const { mockFetch } = await import('./mockApiService');
        return await mockFetch('/subjects/delete-all', 'DELETE');
    }
    try {
        const response = await fetch(`${API_BASE_URL}/subjects/delete-all`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${await _getAuthToken()}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete all subjects');
        }

        return await response.json();
    } catch (error) {
        logger.error('Error deleting all subjects:', error);
        throw error;
    }
};

// --- Institution Settings ---
export const getInstitutionSettings = () => {
    return _internalFetchApi('/institution/settings', 'GET');
};

export const updateInstitutionSettings = (updatedData) => {
    if (!updatedData) throw new Error("Update data is required.");
    const body = {
        name: updatedData.name,
        urlEndpoint: updatedData.urlEndpoint,
        icon: updatedData.icon,
        website: updatedData.website,
        minHour: updatedData.minHour,
        maxHour: updatedData.maxHour,
        activeMonday: updatedData.activeMonday,
        activeTuesday: updatedData.activeTuesday,
        activeWednesday: updatedData.activeWednesday,
        activeThursday: updatedData.activeThursday,
        activeFriday: updatedData.activeFriday,
        activeSaturday: updatedData.activeSaturday,
        activeSunday: updatedData.activeSunday
    };
    return _internalFetchApi('/institution/settings', 'PUT', body);
};

/** Upload a PNG for the school icon. Returns `{ url }`. Does not persist; call updateInstitutionSettings with `icon: url` and save. */
export const uploadInstitutionIconPng = (file) => {
    if (!file) throw new Error('File is required');
    const formData = new FormData();
    formData.append('icon', file);
    return _internalFetchApi('/institution/settings/icon', 'POST', formData);
};

// --- Institution Admins & Invitations ---
export const getInstitutionAdmins = () => {
    return _internalFetchApi('/institution/admins', 'GET');
};

export const inviteInstitutionAdmin = (email) => {
    if (!email) throw new Error('Email is required');
    return _internalFetchApi('/institution/admins/invitations', 'POST', { email });
};

export const resendAdminInvitation = (invitationId) => {
    if (!invitationId) throw new Error('Invitation ID is required');
    return _internalFetchApi(`/institution/admins/invitations/${invitationId}/resend`, 'POST');
};

export const revokeAdminInvitation = (invitationId) => {
    if (!invitationId) throw new Error('Invitation ID is required');
    return _internalFetchApi(`/institution/admins/invitations/${invitationId}`, 'DELETE');
};

export const removeInstitutionAdmin = (userId) => {
    if (!userId) throw new Error('User ID is required');
    return _internalFetchApi(`/institution/admins/${userId}`, 'DELETE');
};

export const acceptInvitation = async (token, firebaseToken) => {
    if (!token) throw new Error('Invitation token is required');
    if (!firebaseToken) throw new Error('Firebase token is required');
    const url = `${API_BASE_URL}/invitations/accept`;
    // Honor mock mode
    if (await configService.isNoServerEnabled()) {
        const { mockFetch } = await import('./mockApiService');
        return await mockFetch('/invitations/accept', 'POST', { token });
    }
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText || 'Accept failed' }));
        const error = new Error(errorData.message);
        error.status = response.status;
        error.code = errorData.code;
        throw error;
    }
    return response.json();
};

export const lookupInvitation = async (token) => {
    if (!token) throw new Error('Invitation token is required');
    const url = `${API_BASE_URL}/invitations/lookup?token=${encodeURIComponent(token)}`;
    // Honor mock mode
    if (await configService.isNoServerEnabled()) {
        const { mockFetch } = await import('./mockApiService');
        return await mockFetch('/invitations/lookup', 'GET', null, { token });
    }
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText || 'Lookup failed' }));
        const error = new Error(errorData.message);
        error.status = response.status;
        throw error;
    }
    return response.json();
};

// --- Schedule Entries ---
export const createScheduleEntry = (scheduleEntry) => {
    if (!scheduleEntry) throw new Error("Schedule entry data is required.");
    
    // Transform the data to match the backend structure
    const transformedData = {
        ...scheduleEntry,
        // Ensure consistent field naming for backend
        entry_type: scheduleEntry.entry_type || scheduleEntry.type,
        start_time: scheduleEntry.start_time || scheduleEntry.timeStart,
        end_time: scheduleEntry.end_time || scheduleEntry.timeEnd,
        week_series: scheduleEntry.week_series || scheduleEntry.weekSeries,
    };
    
    // Remove duplicate frontend fields
    delete transformedData.type;
    delete transformedData.timeStart;
    delete transformedData.timeEnd;
    delete transformedData.weekSeries;
    delete transformedData.time;

    // Prefer IDs over names for associations
    // Include both for debug (ID preferred by backend, name as denormalized)
    if (scheduleEntry.teacher_id) {
        transformedData.teacher_id = scheduleEntry.teacher_id;
    }
    if (scheduleEntry.teacher_name || scheduleEntry.teacher) {
        transformedData.teacher_name = scheduleEntry.teacher_name || scheduleEntry.teacher;
    }
    if (scheduleEntry.subject_id) {
        transformedData.subject_id = scheduleEntry.subject_id;
        delete transformedData.subject_name;
    }
    if (scheduleEntry.room_id) {
        transformedData.room_id = scheduleEntry.room_id;
        delete transformedData.room_name;
    }
    
    return _internalFetchApi('/schedule-entries', 'POST', transformedData);
};

export const updateScheduleEntry = (id, scheduleEntry) => {
    if (!id || !scheduleEntry) throw new Error("Schedule entry ID and data are required.");
    
    // Transform the data to match the backend structure
    const transformedData = {
        ...scheduleEntry,
        entry_type: scheduleEntry.type || 'class', // Transform type to entry_type for backend, with fallback
        // Handle both field naming conventions for time fields
        start_time: scheduleEntry.start_time || scheduleEntry.timeStart,
        end_time: scheduleEntry.end_time || scheduleEntry.timeEnd,
        // Handle both week_series (from AssetManager) and weekSeries (legacy) field names
        week_series: scheduleEntry.week_series || scheduleEntry.weekSeries,
        // Groups are already processed by AssetManager, so just pass them through
        groups: scheduleEntry.groups,
    };
    
    // Remove frontend-specific fields
    delete transformedData.type; // Remove frontend type field
    delete transformedData.timeStart;
    delete transformedData.timeEnd;
    delete transformedData.weekSeries; // Remove camelCase version
    delete transformedData.time; // Remove combined time field to avoid conflicts

    // Prefer IDs over names for associations
    // Include both for debug (ID preferred by backend, name as denormalized)
    if (scheduleEntry.teacher_id) {
        transformedData.teacher_id = scheduleEntry.teacher_id;
    }
    if (scheduleEntry.teacher_name || scheduleEntry.teacher) {
        transformedData.teacher_name = scheduleEntry.teacher_name || scheduleEntry.teacher;
    }
    if (scheduleEntry.subject_id) {
        transformedData.subject_id = scheduleEntry.subject_id;
        delete transformedData.subject_name;
    }
    if (scheduleEntry.room_id) {
        transformedData.room_id = scheduleEntry.room_id;
        delete transformedData.room_name;
    }
    
    return _internalFetchApi(`/schedule-entries/${id}`, 'PUT', transformedData);
};

export const deleteScheduleEntry = (id) => {
    if (!id) throw new Error("Schedule entry ID is required.");
    
    return _internalFetchApi(`/schedule-entries/${id}`, 'DELETE');
};

export const importScheduleEntries = (scheduleEntries) => {
    if (!Array.isArray(scheduleEntries)) throw new Error("Schedule entries must be an array.");
    return _internalFetchApi('/schedule-entries/import', 'POST', scheduleEntries);
};

export const importScheduleEntriesFromCSV = (csvData) => {
    if (typeof csvData !== 'string') throw new Error("CSV data must be a string.");
    // Remove any BOM characters and trim whitespace
    const cleanCsvData = csvData.replace(/^\uFEFF/, '').trim();
    return _internalFetchApi('/schedule-entries/import/csv', 'POST', cleanCsvData, null, {
        headers: {
            'Content-Type': 'text/plain; charset=utf-8'
        }
    });
};

export const importScheduleEntriesFromJSON = (jsonData) => {
    if (!Array.isArray(jsonData)) throw new Error("JSON data must be an array.");
    return _internalFetchApi('/schedule-entries/import/json', 'POST', jsonData, null, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

export const exportScheduleEntries = async (filters = {}) => {
    const queryParams = { ...filters };
    const response = await _internalFetchApi('/schedule-entries', 'GET', null, queryParams);
    return new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
};

/**
 * Sends notifications about schedule changes to affected students.
 * @param {Array} changes - Array of changes, each containing originalEntry and modifiedEntry
 * @returns {Promise<Object>} - Response containing results of the notification process
 * @throws {Error} - If the request fails or if changes is not an array
 */
export const sendScheduleChangeNotifications = (changes) => {
    if (!Array.isArray(changes)) {
        throw new Error("Changes must be an array of { originalEntry, modifiedEntry } objects");
    }
    
    // Validate each change object
    changes.forEach((change, index) => {
        if (!change.originalEntry || !change.modifiedEntry) {
            throw new Error(`Invalid change at index ${index}: must contain originalEntry and modifiedEntry`);
        }
        if (!change.modifiedEntry.group_id) {
            throw new Error(`Invalid change at index ${index}: modifiedEntry must contain group_id`);
        }
    });

    return _internalFetchApi('/schedule-entries/notify', 'POST', { changes });
};

// --- ONBOARDING ---
export const setupInstitution = async (institutionName, userName, firebaseToken) => {
    if (!firebaseToken) throw new Error("Firebase token is required for setup.");
    if (!institutionName || !userName) throw new Error("Institution name and user name are required.");

    const body = { institutionName, name: userName };
    const options = {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    };
    // Use base URL without /api for setup endpoint
    const baseUrl = API_BASE_URL.replace('/api', '');
    const url = `${baseUrl}/setup/institution`;
    logger.debug(`[apiService] Requesting (Setup): POST ${url}`);
    // Honor mock mode
    if (await configService.isNoServerEnabled()) {
        const { mockFetch } = await import('./mockApiService');
        return await mockFetch('/setup/institution', 'POST', body);
    }
    const response = await fetch(url, options);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText || 'Setup failed' }));
        const error = new Error(errorData.message);
        error.status = response.status;
        error.code = errorData.code;
        logger.error(`[apiService] Setup Error Response (${response.status}) from ${url}:`, errorData);
        throw error;
    }
    return response.json();
};

export const deleteAllScheduleEntries = async () => {
    return _internalFetchApi('/schedule-entries/debug/delete-all', 'DELETE');
};

// --- Draft Management ---

export const getDrafts = () => {
    return _internalFetchApi('/schedule-entries/drafts');
};

export const saveDraft = (draftName, year, overwrite = false) => {
    if (!draftName || !year) {
        throw new Error("Draft name and year are required");
    }
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}/save`, 'POST', {
        year,
        overwrite
    });
};

export const loadDraft = (draftName, year, publishOptions = null) => {
    if (!draftName || !year) {
        throw new Error("Draft name and year are required");
    }
    const normalizedOptions = publishOptions && (
        publishOptions.weekRange !== undefined
        || publishOptions.publishMode !== undefined
        || publishOptions.publishStrategy !== undefined
        || publishOptions.reviewDecisions !== undefined
    )
        ? publishOptions
        : { weekRange: publishOptions };
    const publishMode = normalizedOptions?.publishMode || normalizedOptions?.publishStrategy;
    const weekRange = normalizedOptions?.weekRange || (
        normalizedOptions?.startWeek !== undefined && normalizedOptions?.endWeek !== undefined
            ? { startWeek: normalizedOptions.startWeek, endWeek: normalizedOptions.endWeek }
            : null
    );
    const payload = {
        year,
        ...(weekRange && { 
            startWeek: weekRange.startWeek, 
            endWeek: weekRange.endWeek 
        }),
        ...(publishMode && { publishMode }),
        ...(normalizedOptions?.reviewDecisions && { reviewDecisions: normalizedOptions.reviewDecisions })
    };
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}/load`, 'POST', payload);
};

export const renameDraft = (draftName, newName) => {
    if (!draftName || !newName) {
        throw new Error("Draft name and new name are required");
    }
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}/rename`, 'PUT', {
        newName
    });
};

export const deleteDraft = (draftName) => {
    if (!draftName) {
        throw new Error("Draft name is required");
    }
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}`, 'DELETE');
};

export const publishDraftPeriod = (draftName, periodId, year) => {
    if (!draftName || !periodId || !year) {
        throw new Error("Draft name, period ID, and year are required");
    }
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}/publish-period/${periodId}`, 'POST', {
        year
    });
};

export const createEmptyDraft = (draftName, year) => {
    if (!draftName || !year) {
        throw new Error("Draft name and year are required");
    }
    return _internalFetchApi('/schedule-entries/drafts/create_empty', 'POST', {
        draft_name: draftName,
        year
    });
};

export const importIntoDraft = (draftName, fileData, fileType, year) => {
    if (!draftName || !fileData || !fileType || !year) {
        throw new Error("Draft name, file data, file type, and year are required");
    }

    let endpointPath;
    let body;
    let contentType;

    if (fileType === 'csv') {
        endpointPath = `/schedule-entries/import/csv?draft_name=${encodeURIComponent(draftName)}`;
        body = fileData; // Raw CSV string
        contentType = 'text/plain';
    } else if (fileType === 'json') {
        endpointPath = `/schedule-entries/import/json?draft_name=${encodeURIComponent(draftName)}`;
        body = fileData; // JSON array
        contentType = 'application/json';
    } else {
        throw new Error('Unsupported file type. Only CSV and JSON are supported.');
    }

    return _internalFetchApi(endpointPath, 'POST', body, null, {
        headers: {
            'Content-Type': contentType
        }
    });
};

// --- Year Structure ---

/**
 * Fetches the current year structure for the institution
 * @param {number} [academicYearId] - Optional specific academic year ID to retrieve
 * @returns {Promise<Object>} - A promise that resolves to the year structure object
 */
export const getYearStructure = async (academicYearId = null) => {
    const queryParams = academicYearId ? { academicYearId } : {};
    return _internalFetchApi('/year-structure', 'GET', null, queryParams);
};

/**
 * Creates or updates the complete year structure for the institution
 * @param {Object} yearStructureData - The year structure data including academicYear, startWeek, endWeek, periods, holidays
 * @returns {Promise<Object>} - A promise that resolves to the saved year structure object
 */
export const saveYearStructure = (yearStructureData) => {
    if (!yearStructureData || !yearStructureData.academicYear || !yearStructureData.startWeek || !yearStructureData.endWeek) {
        throw new Error("Academic year, start week, and end week are required.");
    }
    
    const body = {
        academicYear: yearStructureData.academicYear,
        startWeek: yearStructureData.startWeek,
        endWeek: yearStructureData.endWeek,
        periods: yearStructureData.periods || [],
        holidays: yearStructureData.holidays || []
    };
    
    return _internalFetchApi('/year-structure', 'POST', body);
};

/**
 * Fetches all academic years for the institution with summary information
 * @returns {Promise<Array>} - A promise that resolves to an array of academic year summary objects
 */
export const getAllAcademicYears = async () => {
    return _internalFetchApi('/year-structure/years', 'GET');
};

/**
 * Deletes a specific academic year and all its associated periods and holidays
 * @param {number} academicYearId - The ID of the academic year to delete
 * @returns {Promise<null>} - A promise that resolves when the academic year is deleted
 */
export const deleteAcademicYear = (academicYearId) => {
    if (!academicYearId) throw new Error("Academic year ID is required for deletion.");
    return _internalFetchApi(`/year-structure/years/${academicYearId}`, 'DELETE');
};

/**
 * Previews what publishing a draft will do without actually publishing
 * @param {string} draftName - The name of the draft to preview
 * @param {number} year - The year to preview publishing for
 * @param {Object} [weekRange] - Optional week range {startWeek, endWeek}
 * @returns {Promise<Object>} - A promise that resolves to the preview data
 */
export const previewDraftPublish = (draftName, year, publishOptions = null) => {
    if (!draftName || !year) {
        throw new Error("Draft name and year are required.");
    }
    const normalizedOptions = publishOptions && (
        publishOptions.weekRange !== undefined
        || publishOptions.publishMode !== undefined
        || publishOptions.publishStrategy !== undefined
    )
        ? publishOptions
        : { weekRange: publishOptions };
    const publishMode = normalizedOptions?.publishMode || normalizedOptions?.publishStrategy;
    const weekRange = normalizedOptions?.weekRange || (
        normalizedOptions?.startWeek !== undefined && normalizedOptions?.endWeek !== undefined
            ? { startWeek: normalizedOptions.startWeek, endWeek: normalizedOptions.endWeek }
            : null
    );
    const body = {
        year,
        ...(publishMode && { publishMode })
    };
    if (weekRange && weekRange.startWeek !== undefined && weekRange.endWeek !== undefined) {
        body.startWeek = weekRange.startWeek;
        body.endWeek = weekRange.endWeek;
    }
    return _internalFetchApi(`/schedule-entries/drafts/${encodeURIComponent(draftName)}/preview-publish`, 'POST', body);
};